'use client'

import { BillSimulation, payBill, simulateBill } from '@/lib/api/financial/digital-account-api'
import { barcodeToDigitableLine, formatDigitableLine, isValidDigitableLine } from '@/lib/financial/payment-code'
import { formatDateOnly } from '@/lib/utils/date-only'
import PaymentCodeScanner from '@/components/code-scanner/payment-code-scanner'
import { useEffect, useState } from 'react'
import { MdPhotoCamera, MdReceiptLong, MdSearch } from 'react-icons/md'
import { toast } from 'react-toastify'
import PasswordField from './password-field'
import CurrencyInput from './currency-input'

const formatBrl = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const toCents = (value?: number | null): number | null =>
    value ? Math.round(Number(value) * 100) : null

/** Aceita linha digitável (47/48) ou código de barras (44) e devolve a linha só com dígitos. */
function normalizeLine(raw: string): string | null {
    const digits = raw.replace(/\D/g, '')
    if (digits.length === 44) return barcodeToDigitableLine(digits)
    return isValidDigitableLine(digits) ? digits : null
}

export default function PayBillModal({
    initialDigitableLine,
    onClose,
    onSuccess,
}: {
    initialDigitableLine?: string
    onClose: () => void
    onSuccess: () => void
}) {
    const [identificationField, setIdentificationField] = useState(
        initialDigitableLine ? formatDigitableLine(initialDigitableLine) : '',
    )
    const [simulated, setSimulated] = useState<BillSimulation | null>(null)
    const [valueCents, setValueCents] = useState<number | null>(null)
    const [description, setDescription] = useState('')
    const [scheduleDate, setScheduleDate] = useState('')
    const [password, setPassword] = useState('')
    const [simulating, setSimulating] = useState(false)
    const [paying, setPaying] = useState(false)
    const [scanning, setScanning] = useState(false)

    const slip = simulated?.bankSlipInfo ?? null
    // O provedor só usa o valor informado quando a conta não traz valor próprio
    // (fatura de cartão, por exemplo) ou permite pagar outro valor.
    const valueEditable = !slip?.value || Boolean(slip?.allowChangeValue)
    const charges = Number(slip?.interestValue ?? 0) + Number(slip?.fineValue ?? 0)
    const discount = Number(slip?.discountValue ?? 0)

    const simulate = async (raw: string) => {
        const line = normalizeLine(raw)
        if (!line) {
            toast.warning('Linha digitável inválida. Confira os números.')
            return
        }
        setIdentificationField(formatDigitableLine(line))
        setSimulating(true)
        try {
            const res = await simulateBill(line)
            if (res.success && res.result) {
                setSimulated(res.result)
                setValueCents(toCents(res.result.bankSlipInfo?.value))
            } else {
                toast.error(res.error || 'Não foi possível consultar a conta')
            }
        } finally {
            setSimulating(false)
        }
    }

    useEffect(() => {
        if (initialDigitableLine) simulate(initialDigitableLine)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleFieldChange = (value: string) => {
        setIdentificationField(value)
        setSimulated(null)
    }

    const handlePay = async () => {
        const line = normalizeLine(identificationField)
        if (!line) return toast.warning('Linha digitável inválida. Confira os números.')
        if (!simulated) return toast.warning('Consulte a conta antes de pagar')
        if (!valueCents || valueCents <= 0) return toast.warning('Informe o valor')
        if (!password) return toast.warning('Confirme sua senha')
        setPaying(true)
        try {
            const res = await payBill({
                identificationField: line,
                value: valueEditable ? valueCents / 100 : undefined,
                description: description.trim() || undefined,
                scheduleDate: scheduleDate || undefined,
                dueDate: slip?.dueDate || undefined,
                password,
            })
            if (res.success) {
                toast.success(scheduleDate ? 'Pagamento agendado' : 'Pagamento enviado')
                onSuccess()
            } else {
                toast.error(res.error || 'Erro ao pagar')
            }
        } finally {
            setPaying(false)
        }
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <h3 className="font-bold text-lg flex items-center gap-2 mb-4">
                    <MdReceiptLong className="text-rose-500" /> Pagar conta
                </h3>

                <div className="flex flex-col gap-1 mb-3">
                    <span className="text-sm font-medium text-base-content/80">Linha digitável ou código de barras</span>
                    <div className="flex gap-2">
                        <input
                            className="input input-bordered input-sm flex-1 font-mono text-xs"
                            placeholder="Digite, cole ou leia com a câmera"
                            inputMode="numeric"
                            autoComplete="off"
                            value={identificationField}
                            onChange={e => handleFieldChange(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && simulate(identificationField)}
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            title="Ler com a câmera"
                            onClick={() => setScanning(true)}
                            disabled={simulating || paying}
                        >
                            <MdPhotoCamera className="text-lg" />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            title="Consultar"
                            onClick={() => simulate(identificationField)}
                            disabled={simulating || !identificationField.trim()}
                        >
                            {simulating ? <span className="loading loading-spinner loading-xs" /> : <MdSearch className="text-lg" />}
                        </button>
                    </div>
                </div>

                {slip && (
                    <div className="rounded-xl border border-base-300 bg-base-200/50 p-3 mb-3 text-sm space-y-1.5">
                        <div className="flex items-start justify-between gap-3">
                            <span className="text-base-content/60">Beneficiário</span>
                            <span className="font-medium text-right">
                                {slip.beneficiaryName || slip.companyName || '—'}
                                {slip.beneficiaryCpfCnpj && (
                                    <span className="block text-xs text-base-content/50 font-normal">{slip.beneficiaryCpfCnpj}</span>
                                )}
                            </span>
                        </div>
                        {slip.dueDate && (
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-base-content/60">Vencimento</span>
                                <span className="flex items-center gap-2">
                                    {formatDateOnly(slip.dueDate)}
                                    {slip.isOverdue && <span className="badge badge-error badge-sm">Vencido</span>}
                                </span>
                            </div>
                        )}
                        {charges > 0 && (
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-base-content/60">Juros e multa</span>
                                <span>{formatBrl(charges)}</span>
                            </div>
                        )}
                        {discount > 0 && (
                            <div className="flex items-center justify-between gap-3">
                                <span className="text-base-content/60">Desconto</span>
                                <span>- {formatBrl(discount)}</span>
                            </div>
                        )}
                        {slip.value ? (
                            <div className="flex items-center justify-between gap-3 pt-1.5 border-t border-base-300">
                                <span className="text-base-content/60">Valor a pagar</span>
                                <span className="font-semibold">{formatBrl(Number(slip.value))}</span>
                            </div>
                        ) : null}
                        {simulated?.fee ? (
                            <p className="text-xs text-base-content/50">Tarifa do pagamento: {formatBrl(Number(simulated.fee))}</p>
                        ) : null}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">Valor</span>
                        <CurrencyInput valueCents={valueCents} onChange={setValueCents} disabled={paying || !valueEditable} />
                        {slip?.allowChangeValue && (slip.minValue || slip.maxValue) ? (
                            <span className="text-xs text-base-content/50">
                                {slip.minValue ? `Mínimo ${formatBrl(Number(slip.minValue))}` : ''}
                                {slip.minValue && slip.maxValue ? ' · ' : ''}
                                {slip.maxValue ? `Máximo ${formatBrl(Number(slip.maxValue))}` : ''}
                            </span>
                        ) : null}
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-base-content/80">Agendar para</span>
                        <input
                            type="date"
                            className="input input-bordered input-sm"
                            value={scheduleDate}
                            min={simulated?.minimumScheduleDate || undefined}
                            onChange={e => setScheduleDate(e.target.value)}
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-1 mt-3">
                    <span className="text-sm font-medium text-base-content/80">Descrição</span>
                    <input className="input input-bordered input-sm" autoComplete="off" value={description} onChange={e => setDescription(e.target.value)} />
                </div>

                <div className="mt-3">
                    <PasswordField value={password} onChange={setPassword} disabled={paying} />
                </div>

                <div className="modal-action">
                    <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={paying}>Cancelar</button>
                    <button className="btn btn-secondary btn-sm" onClick={handlePay} disabled={paying || simulating || !simulated}>
                        {paying ? <span className="loading loading-spinner loading-xs" /> : 'Pagar'}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={() => !paying && onClose()} />

            {scanning && (
                <PaymentCodeScanner
                    accept={['boleto']}
                    onClose={() => setScanning(false)}
                    onDetected={code => {
                        setScanning(false)
                        if (code.kind === 'boleto') simulate(code.digitableLine)
                    }}
                />
            )}
        </div>
    )
}
